import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Mail, Building, Lock, Search, ArrowRight, ArrowLeft, CheckCircle2, User, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import LandingPage from '@/pages/LandingPage';
import Wordmark from '@/components/Wordmark';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
// Gaar gennem VORES backend, ikke direkte til hentcvr.dk. Et browserkald
// derover er cross-origin, og hentcvr tillader kun hentpost.dk — paa
// DigitalBox kastede browseren svaret vaek, og soegningen gav aldrig et
// resultat. Serveren har ingen CORS at forholde sig til, og proxyen
// virker derfor for ethvert brand.
const CVR_SEARCH_URL = `${API}/public/search-company`;

function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  // Step 1: Company
  const [companyQuery, setCompanyQuery] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [companyCvr, setCompanyCvr] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceRef = useRef(null);

  // Step 2: Email + Password
  const [contactName, setContactName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');


  // Company search
  useEffect(() => {
    if (companyCvr) return;
    if (companyQuery.length < 2) { setSuggestions([]); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await axios.get(CVR_SEARCH_URL, { params: { q: companyQuery } });
        const data = Array.isArray(r.data) ? r.data : (r.data?.results ?? r.data?.companies ?? []);
        setSuggestions(data);
        setShowSuggestions(data.length > 0);
      } catch { setSuggestions([]); }
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [companyQuery, companyCvr]);

  const selectCompany = (c) => {
    setCompanyName(c.name);
    setCompanyCvr(c.cvr || '');
    setCompanyQuery(c.name);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  // Ingen koder foer kontoen findes. Verifikationen sker inde i appen bagefter:
  // et kodetrin her ville staa mellem kunden og en konto, hun endnu ikke har
  // grund til at kaempe for. Backenden sender bekraeftelsesmailen automatisk.
  const handleRegister = async () => {
    if (!email || !password) { toast.error('Udfyld email og password'); return; }
    if (password.length < 6) { toast.error('Password skal være mindst 6 tegn'); return; }
    setLoading(true);
    try {
      await register(email, password, companyName, companyCvr, contactName, phone);
      toast.success('Konto oprettet!');
      navigate('/dashboard');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Noget gik galt. Prøv igen.');
    } finally { setLoading(false); }
  };

  const steps = [
    { num: 1, label: 'Vælg virksomhed' },
    { num: 2, label: 'Opret konto' },
  ];

  return (
    <div className="relative min-h-screen">
      {/* Landing page blurred behind */}
      <div className="fixed inset-0 overflow-auto blur-sm brightness-50 pointer-events-none">
        <LandingPage />
      </div>

      {/* Modal overlay */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-[var(--surface-overlay)]" onClick={() => navigate('/')} />
        <Card className="relative z-10 w-full max-w-md p-8 bg-th-sidebar border-th-border shadow-2xl shadow-brand-500/10">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <a href="/" className="flex items-center gap-3">
              <Mail size={40} className="text-brand-500" />
              <span className="text-2xl font-heading font-bold text-th-text">
                <Wordmark />
              </span>
            </a>
          </div>

          {/* Progress */}
          <div className="flex items-center justify-center gap-2 mb-8">
            {steps.map((s, i) => (
              <React.Fragment key={s.num}>
                <div className={`flex items-center gap-2 ${step >= s.num ? 'text-brand-400' : 'text-th-text-faint'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                    step > s.num ? 'bg-brand-500 text-white' :
                    step === s.num ? 'bg-brand-500/20 text-brand-400 ring-2 ring-brand-500' :
                    'bg-th-hover text-th-text-muted'
                  }`}>
                    {step > s.num ? <CheckCircle2 size={16} /> : s.num}
                  </div>
                  <span className="text-xs font-medium hidden sm:inline">{s.label}</span>
                </div>
                {i < steps.length - 1 && <div className={`w-8 h-0.5 ${step > s.num ? 'bg-brand-500' : 'bg-th-hover'}`} />}
              </React.Fragment>
            ))}
          </div>

          {/* Step 1: Virksomhed */}
          {step === 1 && (
            <div>
              <h1 className="text-2xl font-heading font-bold text-th-text text-center mb-2">Find din virksomhed</h1>
              <p className="text-th-text-secondary text-center mb-8 text-sm">Søg efter navn eller CVR-nummer</p>

              <div className="space-y-4">
                {companyCvr ? (
                  <>
                    <div className="p-5 bg-emerald-500/5 border border-emerald-500/20 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <CheckCircle2 size={24} className="text-emerald-500" />
                          <div>
                            <p className="text-th-text font-semibold">{companyName}</p>
                            <p className="text-sm text-th-text-secondary">CVR: {companyCvr}</p>
                          </div>
                        </div>
                        <button onClick={() => { setCompanyCvr(''); setCompanyName(''); setCompanyQuery(''); }}
                          className="text-xs text-th-text-muted hover:text-th-text-secondary transition-colors">Skift</button>
                      </div>
                    </div>
                    <Button onClick={() => setStep(2)} className="w-full h-12 bg-btn hover:bg-btn-hover text-btn-fg text-lg">
                      Næste <ArrowRight size={20} className="ml-2" />
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="relative">
                      <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-th-text-muted" />
                      <Input type="text" value={companyQuery} autoFocus
                        onChange={(e) => { setCompanyQuery(e.target.value); setCompanyName(e.target.value); setCompanyCvr(''); }}
                        onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                        placeholder="Søg virksomhed eller CVR..."
                        className="pl-10 h-12 bg-th-input border-th-border-light text-th-text placeholder:text-th-text-muted text-lg" />

                      {showSuggestions && suggestions.length > 0 && (
                        <div className="absolute top-full left-0 right-0 mt-1 bg-th-input border border-th-border-light rounded-lg shadow-xl z-[100] overflow-hidden">
                          {suggestions.map((c, i) => (
                            <button key={i} type="button" onClick={() => selectCompany(c)}
                              className="w-full text-left px-4 py-3 hover:bg-th-hover transition-colors border-b border-th-border last:border-0">
                              <div className="flex items-center gap-3">
                                <div className="p-1.5 bg-brand-500/10 rounded"><Building size={16} className="text-brand-500" /></div>
                                <div>
                                  <p className="text-th-text font-medium text-sm">{c.name}</p>
                                  <p className="text-xs text-th-text-secondary">{c.cvr && `CVR: ${c.cvr}`}{c.city && ` · ${c.city}`}</p>
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    <button onClick={() => { setCompanyName(''); setCompanyCvr(''); setStep(2); }}
                      className="w-full text-sm text-th-text-muted hover:text-th-text-secondary transition-colors">
                      Spring over — jeg har ikke en virksomhed
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Email + Password */}
          {step === 2 && (
            <div>
              <h1 className="text-2xl font-heading font-bold text-th-text text-center mb-2">Opret din konto</h1>
              <p className="text-th-text-secondary text-center mb-8 text-sm">
                {companyName ? `For ${companyName}` : 'Indtast dine loginoplysninger'}
              </p>

              <div className="space-y-4">
                <div className="relative">
                  <User size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-th-text-muted" />
                  <Input type="text" value={contactName} onChange={(e) => setContactName(e.target.value)} autoFocus
                    placeholder="Dit navn (kontaktperson)"
                    className="pl-10 h-12 bg-th-input border-th-border-light text-th-text placeholder:text-th-text-muted" />
                </div>

                <div className="relative">
                  <Mail size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-th-text-muted" />
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="din@email.dk"
                    className="pl-10 h-12 bg-th-input border-th-border-light text-th-text placeholder:text-th-text-muted" />
                </div>

                <div className="relative">
                  <Lock size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-th-text-muted" />
                  <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder="Vælg password (min. 6 tegn)"
                    className="pl-10 h-12 bg-th-input border-th-border-light text-th-text placeholder:text-th-text-muted" />
                </div>

                <div className="relative">
                  <Phone size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-th-text-muted" />
                  <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                    placeholder="Mobilnummer"
                    className="pl-10 h-12 bg-th-input border-th-border-light text-th-text placeholder:text-th-text-muted" />
                </div>

                <Button onClick={handleRegister} disabled={!email || !password || loading}
                  className="w-full h-12 bg-btn hover:bg-btn-hover text-btn-fg text-lg">
                  {loading ? 'Opretter konto...' : 'Opret konto gratis'}
                </Button>

                <button onClick={() => setStep(1)} className="w-full flex items-center justify-center gap-2 text-sm text-th-text-muted hover:text-th-text-secondary transition-colors">
                  <ArrowLeft size={16} /> Tilbage
                </button>
              </div>
            </div>
          )}

          {/* Login link */}
          <div className="mt-6 text-center">
            <p className="text-th-text-secondary text-sm">
              Har du allerede en konto?{' '}
              <Link to="/login" className="text-brand-500 hover:text-brand-400 font-medium">Log ind</Link>
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}

export default RegisterPage;
